import styled from "styled-components";
import { useEffect } from "react";
import { SideSheetContainer } from "@cdkglobal/radial";
import { StyledSideSheetBody } from "../../../../utils/styled";
import { RowDataReducerAction, RowDataReducerState } from "../../../../types";
import RecordPage from "../record-page/RecordPage";
import SideSheetTopBar from "./SideSheetTopBar";
import SideSheetFooter from "./SideSheetFooter";
import { useUserContext } from "../../../../contexts/UserContext";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import { getSideSheetTitle } from "../../helpers";
import { useIntl } from "react-intl";

// cdk-radial's SideSheetContainer doesn't expose a flex-column style by
// default — wrap it so top/body/footer stack correctly and body can scroll.
const FlexSideSheetContainer = styled(SideSheetContainer)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: stretch;
  border-start-end-radius: inherit;
  border-bottom-right-radius: inherit;
  margin: ${({ isCollapsed }) =>
    isCollapsed ? "0rem" : "-0.5rem -1rem 0rem 0rem"};
  padding-right: ${({ isCollapsed }) => (isCollapsed ? "0rem" : "0.5rem")};
  z-index: 1000;
`;

interface RecordSideSheetProps {
  isSideSheetOpen: boolean;
  rowDataReducerState: RowDataReducerState;
  dispatch: React.Dispatch<RowDataReducerAction>;
}

export default function RecordSideSheet({
  isSideSheetOpen,
  rowDataReducerState,
  dispatch,
}: Readonly<RecordSideSheetProps>): JSX.Element {
  const intl = useIntl();
  const { selected } = useUserContext();
  const { setActiveTabIndex } = useUIStateContext();

  // Reset tab index to 0 when side sheet opens
  useEffect(() => {
    if (isSideSheetOpen) {
      setActiveTabIndex(0);
    }
  }, [isSideSheetOpen, setActiveTabIndex]);

  return (
    <FlexSideSheetContainer
      isCollapsed={!isSideSheetOpen}
      isModal
      width={isSideSheetOpen ? "620px" : "0px"}
      dataTestId="record-side-sheet"
    >
      <SideSheetTopBar
        title={getSideSheetTitle({
          rowData: rowDataReducerState.rowData,
          selected,
          isReadOnly: rowDataReducerState.isReadOnly,
          intl,
        })}
      />

      <StyledSideSheetBody>
        {isSideSheetOpen && (
          <RecordPage
            rowDataReducerState={rowDataReducerState}
            dispatch={dispatch}
          />
        )}
      </StyledSideSheetBody>

      <SideSheetFooter
        rowDataReducerState={rowDataReducerState}
        dispatch={dispatch}
      />
    </FlexSideSheetContainer>
  );
}
